/**
 * About Section Component
 *
 * React implementation of the about section renderer.
 * Supports variants: story, team, mission, timeline
 */

import { AboutData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface AboutProps extends SectionProps<AboutData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function About({ data, ctx, sectionId, elements }: AboutProps) {
  const variant = data.variant || 'story';

  switch (variant) {
    case 'team':
      return <AboutTeam data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'mission':
      return <AboutMission data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'timeline':
      return <AboutTimeline data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <AboutStory data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// STORY VARIANT
// =============================================================================

function AboutStory({ data, ctx, elements }: AboutProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const contentEl = findElement(elements, 'content');
  const imageEl = findElement(elements, 'image');
  const founderNameEl = findElement(elements, 'founderName');
  const founderTitleEl = findElement(elements, 'founderTitle');
  const founderImageEl = findElement(elements, 'founderImage');

  return (
    <section
      id="about"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content Side */}
          <div>
            {/* Eyebrow */}
            {(data.eyebrow || eyebrowEl) && (
              <div className="mb-4">
                {eyebrowEl ? (
                  <EditableText
                    element={eyebrowEl}
                    as="span"
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  />
                ) : (
                  <span
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  >
                    {data.eyebrow}
                  </span>
                )}
              </div>
            )}

            {/* Headline */}
            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                  fontWeight: recipe.typography.headingWeight,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                  fontWeight: recipe.typography.headingWeight,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {/* Subheadline */}
            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-6"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-6" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            {/* Content */}
            {contentEl ? (
              <EditableText
                element={contentEl}
                as="div"
                className="prose prose-lg max-w-none mb-8"
                style={{ color: recipe.colors.text }}
              />
            ) : data.content ? (
              <div
                className="prose prose-lg max-w-none mb-8"
                style={{ color: recipe.colors.text }}
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            ) : null}

            {/* Founder Info */}
            {(data.founderName || founderNameEl) && (
              <div className="flex items-center gap-4 mt-8 pt-8 border-t" style={{ borderColor: `${recipe.colors.textMuted}20` }}>
                {(data.founderImage || founderImageEl) && (
                  <div className="w-16 h-16 rounded-full overflow-hidden">
                    {founderImageEl ? (
                      <EditableImage
                        element={founderImageEl}
                        alt={data.founderName || 'Founder'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={data.founderImage}
                        alt={data.founderName || 'Founder'}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                )}
                <div>
                  {founderNameEl ? (
                    <EditableText
                      element={founderNameEl}
                      as="p"
                      className="font-semibold"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>
                      {data.founderName}
                    </p>
                  )}
                  {(data.founderTitle || founderTitleEl) && (
                    founderTitleEl ? (
                      <EditableText
                        element={founderTitleEl}
                        as="p"
                        className="text-sm"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                        {data.founderTitle}
                      </p>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Stats */}
            {data.stats && data.stats.length > 0 && (
              <div className="grid grid-cols-3 gap-6 mt-10">
                {data.stats.map((stat, i) => {
                  const statValueEl = findElement(elements, `stats.${i}.value`);
                  const statLabelEl = findElement(elements, `stats.${i}.label`);

                  return (
                    <div key={i} className="text-center">
                      {statValueEl ? (
                        <EditableText
                          element={statValueEl}
                          as="p"
                          className="text-3xl font-bold"
                          style={{ color: recipe.colors.primary }}
                        />
                      ) : (
                        <p className="text-3xl font-bold" style={{ color: recipe.colors.primary }}>
                          {stat.value}
                        </p>
                      )}
                      {statLabelEl ? (
                        <EditableText
                          element={statLabelEl}
                          as="p"
                          className="text-sm mt-1"
                          style={{ color: recipe.colors.textMuted }}
                        />
                      ) : (
                        <p className="text-sm mt-1" style={{ color: recipe.colors.textMuted }}>
                          {stat.label}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Image Side */}
          <div className="relative">
            {imageEl ? (
              <EditableImage
                element={imageEl}
                alt={data.headline || 'About us'}
                className="w-full rounded-2xl shadow-xl"
                style={{ borderRadius: recipe.borders.imageRadius }}
              />
            ) : data.image ? (
              <img
                src={data.image}
                alt={data.headline || 'About us'}
                className="w-full rounded-2xl shadow-xl"
                style={{ borderRadius: recipe.borders.imageRadius }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// TEAM VARIANT
// =============================================================================

function AboutTeam({ data, ctx, elements }: AboutProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="about"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Team Grid */}
        {data.team && data.team.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {data.team.map((member, i) => {
              const memberNameEl = findElement(elements, `team.${i}.name`);
              const memberTitleEl = findElement(elements, `team.${i}.title`);
              const memberImageEl = findElement(elements, `team.${i}.image`);
              const memberBioEl = findElement(elements, `team.${i}.bio`);

              return (
                <div
                  key={i}
                  className="group text-center p-6 rounded-xl transition-shadow"
                  style={{
                    backgroundColor: recipe.colors.background,
                    borderRadius: recipe.borders.cardRadius,
                    boxShadow: recipe.shadows.card,
                  }}
                >
                  {/* Member Photo */}
                  <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden">
                    {memberImageEl ? (
                      <EditableImage
                        element={memberImageEl}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-3xl font-bold"
                        style={{ backgroundColor: recipe.colors.primary, color: 'white' }}
                      >
                        {member.name.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Member Info */}
                  {memberNameEl ? (
                    <EditableText
                      element={memberNameEl}
                      as="h3"
                      className="text-lg font-semibold mb-1"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <h3 className="text-lg font-semibold mb-1" style={{ color: recipe.colors.text }}>
                      {member.name}
                    </h3>
                  )}

                  {memberTitleEl ? (
                    <EditableText
                      element={memberTitleEl}
                      as="p"
                      className="text-sm mb-3"
                      style={{ color: recipe.colors.primary }}
                    />
                  ) : member.title ? (
                    <p className="text-sm mb-3" style={{ color: recipe.colors.primary }}>
                      {member.title}
                    </p>
                  ) : null}

                  {memberBioEl ? (
                    <EditableText
                      element={memberBioEl}
                      as="p"
                      className="text-sm"
                      style={{ color: recipe.colors.textMuted }}
                    />
                  ) : member.bio ? (
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                      {member.bio}
                    </p>
                  ) : null}

                  {/* Social Links */}
                  {member.social && (
                    <div className="flex justify-center gap-3 mt-4">
                      {member.social.linkedin && (
                        <a
                          href={member.social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                        </a>
                      )}
                      {member.social.twitter && (
                        <a
                          href={member.social.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-400 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                          </svg>
                        </a>
                      )}
                      {member.social.email && (
                        <a
                          href={`mailto:${member.social.email}`}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// MISSION VARIANT
// =============================================================================

function AboutMission({ data, ctx, elements }: AboutProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const contentEl = findElement(elements, 'content');

  return (
    <section
      id="about"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.primary,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Eyebrow */}
        {(data.eyebrow || eyebrowEl) && (
          <div className="mb-4">
            {eyebrowEl ? (
              <EditableText
                element={eyebrowEl}
                as="span"
                className="text-sm font-semibold uppercase tracking-wider text-white/70"
              />
            ) : (
              <span className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {data.eyebrow}
              </span>
            )}
          </div>
        )}

        {/* Headline */}
        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h2"
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
            style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
          />
        ) : data.headline ? (
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
            style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
          >
            {data.headline}
          </h2>
        ) : null}

        {/* Subheadline */}
        {subheadlineEl ? (
          <EditableText
            element={subheadlineEl}
            as="p"
            className="text-xl text-white/80 mb-8"
          />
        ) : data.subheadline ? (
          <p className="text-xl text-white/80 mb-8">{data.subheadline}</p>
        ) : null}

        {/* Content */}
        {contentEl ? (
          <EditableText
            element={contentEl}
            as="div"
            className="prose prose-lg prose-invert max-w-none text-white/90"
          />
        ) : data.content ? (
          <div
            className="prose prose-lg prose-invert max-w-none text-white/90"
            dangerouslySetInnerHTML={{ __html: data.content }}
          />
        ) : null}

        {/* Values */}
        {data.values && data.values.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {data.values.map((value, i) => {
              const valueTitleEl = findElement(elements, `values.${i}.title`);
              const valueDescEl = findElement(elements, `values.${i}.description`);

              return (
                <div key={i} className="text-left p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                  {value.icon && (
                    <div className="text-3xl mb-3">{value.icon}</div>
                  )}
                  {valueTitleEl ? (
                    <EditableText
                      element={valueTitleEl}
                      as="h3"
                      className="text-lg font-semibold text-white mb-2"
                    />
                  ) : (
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {value.title}
                    </h3>
                  )}
                  {valueDescEl ? (
                    <EditableText
                      element={valueDescEl}
                      as="p"
                      className="text-white/70"
                    />
                  ) : (
                    <p className="text-white/70">{value.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {data.cta && (
          <div className="mt-10">
            <a
              href={data.cta.url}
              className="inline-flex items-center px-6 py-3 bg-white text-lg font-semibold rounded-lg transition-transform hover:scale-105"
              style={{ color: recipe.colors.primary, borderRadius: recipe.borders.buttonRadius }}
            >
              {data.cta.text}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// TIMELINE VARIANT
// =============================================================================

function AboutTimeline({ data, ctx, elements }: AboutProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="about"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Timeline */}
        {data.timeline && data.timeline.length > 0 && (
          <div className="relative">
            {/* Vertical line */}
            <div
              className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full"
              style={{ backgroundColor: recipe.colors.primary + '30' }}
            />

            <div className="space-y-12">
              {data.timeline.map((item, i) => {
                const yearEl = findElement(elements, `timeline.${i}.year`);
                const titleEl = findElement(elements, `timeline.${i}.title`);
                const descEl = findElement(elements, `timeline.${i}.description`);
                const isLeft = i % 2 === 0;

                return (
                  <div key={i} className={`relative flex items-center ${isLeft ? 'justify-start' : 'justify-end'}`}>
                    {/* Content */}
                    <div className={`w-5/12 ${isLeft ? 'text-right pr-8' : 'text-left pl-8'}`}>
                      {yearEl ? (
                        <EditableText
                          element={yearEl}
                          as="span"
                          className="text-sm font-bold"
                          style={{ color: recipe.colors.primary }}
                        />
                      ) : (
                        <span className="text-sm font-bold" style={{ color: recipe.colors.primary }}>
                          {item.year}
                        </span>
                      )}
                      {titleEl ? (
                        <EditableText
                          element={titleEl}
                          as="h3"
                          className="text-xl font-semibold mt-1 mb-2"
                          style={{ color: recipe.colors.text }}
                        />
                      ) : (
                        <h3 className="text-xl font-semibold mt-1 mb-2" style={{ color: recipe.colors.text }}>
                          {item.title}
                        </h3>
                      )}
                      {descEl ? (
                        <EditableText
                          element={descEl}
                          as="p"
                          style={{ color: recipe.colors.textMuted }}
                        />
                      ) : (
                        <p style={{ color: recipe.colors.textMuted }}>{item.description}</p>
                      )}
                    </div>

                    {/* Center dot */}
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full border-4"
                      style={{
                        backgroundColor: recipe.colors.background,
                        borderColor: recipe.colors.primary,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default About;
